/*
 * Cottomix theme — quick view (cm-quick-view.js)
 *
 * Purpose: drives the cm-quick-view modal (contracts C3 + C7). Fetches the
 * product markup once per handle+variant with the Section Rendering API
 * (?section_id=quick-view), caches the parsed node, and renders it with
 * importNode + replaceChildren (no raw HTML injection). Option changes swap only
 * the info block; add-to-cart goes through window.CM.cart.add. If the modal
 * shell is missing, open() falls back to navigating to the product page.
 * Nothing is invented: every visible string comes from the JSON tag the
 * section renders (data-cm-qv-json).
 *
 * Contracts: C3 (bus events), C7 (section quick-view, modal id cm-quick-view,
 * fetch URL <root_url>products/<handle>?section_id=quick-view [&variant=id]).
 *
 * Events (window.CM bus):
 *   quickview:open {handle}   — open the modal and load that product
 *   modal:close {id}          — id cm-quick-view: bump the token, abort the
 *                               in-flight fetch (stale responses are dropped)
 *   cart:updated              — clear the response cache (price/stock may
 *                               have changed)
 *   cart:open                 — emitted after a successful quick-view add
 *
 * Attributes consumed: data-cm-qv-body, -skeleton, -content, -error, -retry,
 * -template, -product, -info, -form, -variant-id, -variants, -add (+
 * data-label-add / -adding / -added), -message, -json, -gallery, -slides,
 * -slide, -dots, -dot (+ data-index); radios input[type=radio][data-position];
 * state classes .is-active (dots), .is-updating (info), .is-loading /
 * .is-added (add button).
 */
(function () {
  'use strict';

  if (window.__cmQuickViewLoaded) return;
  window.__cmQuickViewLoaded = true;

  /* ------------------------------------------------------------ *
   * Constants + helpers
   * ------------------------------------------------------------ */
  var HANDLE_RE = /^[^\s\/?#&<>"'\\]{1,255}$/;
  var CONFIRM_MS = 600; /* UI confirmation pause after a successful add */
  var cache = new Map(); /* key = handle + '|' + (variantId || '') */
  var token = 0; /* bumped on every load; older responses are ignored */
  var current = null; /* { handle, variantId } of the open product */
  var controller = null; /* AbortController of the in-flight fetch */
  var strings = {};
  var stringsRead = false;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function getStrings() {
    if (stringsRead) return strings;
    stringsRead = true;
    try {
      var el = document.querySelector('[data-cm-qv-json]');
      if (el) strings = JSON.parse(el.textContent) || {};
    } catch (err) {
      strings = {};
    }
    return strings;
  }

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function rootUrl() {
    var b = (window.CM && window.CM.config && window.CM.config.routes &&
      window.CM.config.routes.root_url) || '/';
    return String(b).replace(/\/$/, '') + '/';
  }

  /* ------------------------------------------------------------ *
   * Modal + UI state
   * ------------------------------------------------------------ */
  function getModal() {
    return document.getElementById('cm-quick-view');
  }

  function setBusy(modal, busy) {
    var body = $('[data-cm-qv-body]', modal);
    if (body) body.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function showSkeleton(modal) {
    var sk = $('[data-cm-qv-skeleton]', modal);
    var content = $('[data-cm-qv-content]', modal);
    var error = $('[data-cm-qv-error]', modal);
    if (sk) sk.removeAttribute('hidden');
    if (content) content.setAttribute('hidden', '');
    if (error) error.setAttribute('hidden', '');
    var st = $('[data-cm-qv-status]', modal);
    if (st) st.textContent = '';
    setBusy(modal, true);
  }

  function showContent(modal) {
    var sk = $('[data-cm-qv-skeleton]', modal);
    var content = $('[data-cm-qv-content]', modal);
    var error = $('[data-cm-qv-error]', modal);
    if (sk) sk.setAttribute('hidden', '');
    if (content) content.removeAttribute('hidden');
    if (error) error.setAttribute('hidden', '');
    setBusy(modal, false);
  }

  function showError(modal) {
    var sk = $('[data-cm-qv-skeleton]', modal);
    var content = $('[data-cm-qv-content]', modal);
    var error = $('[data-cm-qv-error]', modal);
    if (sk) sk.setAttribute('hidden', '');
    if (content) content.setAttribute('hidden', '');
    if (error) error.removeAttribute('hidden');
    setBusy(modal, false);
  }

  /* ------------------------------------------------------------ *
   * Fetch (Section Rendering API)
   * Returns the parsed [data-cm-qv-product] node; callers must
   * import it with document.importNode(node, true).
   * ------------------------------------------------------------ */
  async function fetchProduct(handle, variantId, signal) {
    var url = rootUrl() + 'products/' + encodeURIComponent(handle) +
      '?section_id=quick-view' +
      (variantId ? '&variant=' + encodeURIComponent(variantId) : '');
    var res = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      signal: signal
    });
    if (!res.ok) throw new Error('http ' + res.status);
    var text = await res.text();
    var doc = window.CM && window.CM.parseHTML
      ? window.CM.parseHTML(text)
      : new DOMParser().parseFromString(text, 'text/html');
    var tpl = doc.querySelector('template[data-cm-qv-template]');
    if (!tpl) throw new Error('no template');
    var node = tpl.content.querySelector('[data-cm-qv-product]');
    if (!node) throw new Error('no product');
    return node;
  }

  /* ------------------------------------------------------------ *
   * Load + render
   * ------------------------------------------------------------ */
  async function load(handle, variantId, opts) {
    var modal = getModal();
    if (!modal) return;
    var infoOnly = !!(opts && opts.infoOnly);
    var my = ++token;
    if (controller) controller.abort();
    controller = new AbortController();
    var key = handle + '|' + (variantId || '');
    if (!infoOnly) showSkeleton(modal);
    try {
      var node = cache.get(key);
      if (!node) {
        node = await fetchProduct(handle, variantId, controller.signal);
        cache.set(key, node);
      }
      if (my !== token) return;
      if (infoOnly) swapInfo(modal, node);
      else render(modal, node);
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      if (my !== token) return;
      if (infoOnly) {
        /* Restore the info block and surface the error message. */
        if (opts && opts.previous !== undefined) current.variantId = opts.previous;
        var info = $('[data-cm-qv-info]', modal);
        if (info) {
          info.classList.remove('is-updating');
          info.removeAttribute('aria-busy');
        }
        var msg = $('[data-cm-qv-message]', modal);
        if (msg) {
          msg.textContent = getStrings().error || '';
          msg.hidden = false;
        }
      } else {
        showError(modal);
      }
    }
  }

  function render(modal, node) {
    var content = $('[data-cm-qv-content]', modal);
    var panel = $('.cm-qv__panel', modal);
    if (!content) return;
    content.replaceChildren(document.importNode(node, true));
    showContent(modal);
    var stEl = $('[data-cm-qv-status]', modal);
    var titleEl = $('.cm-qv__title', content);
    if (stEl) stEl.textContent = titleEl ? titleEl.textContent : '';
    initGallery(content);
    /* window.CM.reveal is intentionally not called: the content is inserted
       while the modal is open, so there is nothing new to reveal. */
    if (panel) panel.scrollTop = 0;
    if (modal.hasAttribute('open')) {
      var active = document.activeElement;
      if (!active || !panel || !panel.contains(active)) {
        var close = $('.cm-qv__close', modal);
        if (close) close.focus();
      }
    }
  }

  /* Scroll only the gallery's own scroller (never the sheet body / page):
     >=768px the media column scrolls vertically, below it the slides row scrolls horizontally. */
  function scrollToSlide(modal, slide) {
    var behavior = reduced ? 'auto' : 'smooth';
    var slidesEl = $('[data-cm-qv-slides]', modal);
    var col = $('.cm-qv__media', modal);
    if (window.matchMedia('(min-width: 768px)').matches) {
      if (!col) return;
      var pad = parseFloat(getComputedStyle(col).paddingTop) || 0;
      col.scrollBy({ top: slide.getBoundingClientRect().top - col.getBoundingClientRect().top - pad, behavior: behavior });
    } else {
      if (!slidesEl) return;
      slidesEl.scrollBy({ left: slide.getBoundingClientRect().left - slidesEl.getBoundingClientRect().left, behavior: behavior });
    }
  }

  function showVariantImage(modal) {
    var info = $('[data-cm-qv-info]', modal);
    var id = info && info.dataset.featuredImageId;
    if (!id) return;
    var slide = modal.querySelector('[data-cm-qv-slide][data-image-id="' + CSS.escape(id) + '"]');
    if (!slide) return;
    scrollToSlide(modal, slide);
    var dotsAll = modal.querySelectorAll('[data-cm-qv-dot]');
    var slidesAll = modal.querySelectorAll('[data-cm-qv-slide]');
    var at = Array.prototype.indexOf.call(slidesAll, slide);
    dotsAll.forEach(function (d, n) {
      d.classList.toggle('is-active', n === at);
      if (n === at) d.setAttribute('aria-current', 'true');
      else d.removeAttribute('aria-current');
    });
  }

  function swapInfo(modal, node) {
    var oldInfo = $('[data-cm-qv-info]', modal);
    var newInfo = node.querySelector('[data-cm-qv-info]');
    if (!oldInfo || !newInfo) return;
    var active = document.activeElement;
    var sel = null;
    if (active && active.matches && active.matches('input[type="radio"][data-position]')) {
      sel = { position: active.dataset.position, value: active.value };
    }
    oldInfo.replaceWith(document.importNode(newInfo, true));
    if (sel) {
      var radios = modal.querySelectorAll('input[type="radio"][data-position]');
      for (var i = 0; i < radios.length; i += 1) {
        if (radios[i].dataset.position === sel.position && radios[i].value === sel.value) {
          radios[i].focus();
          break;
        }
      }
    }
    showVariantImage(modal);
  }

  /* ------------------------------------------------------------ *
   * Gallery (scroll-snap slides + dots)
   * ------------------------------------------------------------ */
  function initGallery(root) {
    var slidesEl = $('[data-cm-qv-slides]', root);
    var dots = root.querySelectorAll('[data-cm-qv-dot]');
    if (!slidesEl || dots.length < 2) return;
    var slides = root.querySelectorAll('[data-cm-qv-slide]');

    function setActive(i) {
      dots.forEach(function (d, n) {
        var on = n === i;
        d.classList.toggle('is-active', on);
        if (on) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting && e.intersectionRatio >= 0.6) {
            setActive(Array.prototype.indexOf.call(slides, e.target));
          }
        });
      }, { root: slidesEl, threshold: [0.6] });
      slides.forEach(function (s) { io.observe(s); });
    }

    var dotsWrap = $('[data-cm-qv-dots]', root);
    if (!dotsWrap) return;
    dotsWrap.addEventListener('click', function (event) {
      var dot = event.target.closest('[data-cm-qv-dot]');
      if (!dot) return;
      var idx = Number(dot.dataset.index);
      if (!slides[idx]) return;
      scrollToSlide(getModal(), slides[idx]);
      setActive(idx);
    });
  }

  /* ------------------------------------------------------------ *
   * Open
   * ------------------------------------------------------------ */
  function open(handle, variantId) {
    if (typeof handle !== 'string' || !HANDLE_RE.test(handle)) return;
    var modal = getModal();
    if (!modal || typeof modal.open !== 'function') {
      /* Graceful fallback: no modal shell -> go to the product page. */
      window.location.href = rootUrl() + 'products/' + encodeURIComponent(handle);
      return;
    }
    current = { handle: handle, variantId: variantId || '' };
    modal.open();
    load(handle, variantId || '', { infoOnly: false });
  }

  /* ------------------------------------------------------------ *
   * Bus events
   * ------------------------------------------------------------ */
  function whenCM(fn) {
    if (window.CM) {
      fn();
      return;
    }
    document.addEventListener('DOMContentLoaded', function () {
      if (window.CM) fn();
    });
  }

  whenCM(function () {
    window.CM.on('quickview:open', function (d) {
      if (d && d.handle) open(d.handle);
    });
    window.CM.on('modal:close', function (d) {
      if (d && d.id === 'cm-quick-view') {
        token += 1;
        if (controller) controller.abort();
        var m = getModal();
        var skEl = m && $('[data-cm-qv-skeleton]', m);
        if (skEl) skEl.setAttribute('hidden', '');
      }
    });
    window.CM.on('cart:updated', function () {
      cache.clear(); /* price / stock may have changed */
    });
  });

  /* ------------------------------------------------------------ *
   * Delegated DOM listeners (document-level, scoped to the modal)
   * ------------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest('#cm-quick-view [data-cm-qv-retry]')) return;
    if (current) load(current.handle, current.variantId, { infoOnly: false });
  });

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.matches) return;
    if (!input.matches('input[type="radio"][data-position]')) return;
    var form = input.closest('[data-cm-qv-form]');
    if (!form || !form.closest('#cm-quick-view')) return;
    var modal = getModal();
    if (!modal || !current) return;

    var variants = [];
    try {
      var dataEl = $('[data-cm-qv-variants]', form);
      if (dataEl) variants = JSON.parse(dataEl.textContent) || [];
    } catch (err) {
      variants = [];
    }
    if (!Array.isArray(variants) || !variants.length) return;

    /* CRO item 13: while the size group is still unselected (the card opens with no size), a
       colour change keeps the gate: nothing reloads and no size is picked for the shopper. */
    var sizeGroup = form.querySelector('[data-cm-qv-option][data-kind="size"]');
    if (sizeGroup && !sizeGroup.querySelector('input[type="radio"]:checked')) return;

    /* Value of each option group, in order. */
    var selected = [];
    form.querySelectorAll('[data-cm-qv-option]').forEach(function (group) {
      var checked = group.querySelector('input[type="radio"]:checked');
      selected.push(checked ? checked.value : null);
    });

    var variant = variants.find(function (v) {
      return v && v.options && v.options.every(function (o, i) {
        return o === selected[i];
      });
    });

    if (!variant) {
      /* No exact match: keep the picked option, complete the rest —
         first match wins unless a later one is available. */
      var pos = Number(input.dataset.position);
      for (var i = 0; i < variants.length; i += 1) {
        var v = variants[i];
        if (!v || !v.options || v.options[pos - 1] !== input.value) continue;
        if (!variant) variant = v;
        if (v.available) { variant = v; break; }
      }
    }

    if (!variant) {
      var m = $('[data-cm-qv-message]', modal);
      if (m) {
        m.textContent = getStrings().variantUnavailable || '';
        m.hidden = false;
      }
      return;
    }

    var info = $('[data-cm-qv-info]', modal);
    if (info) {
      info.classList.add('is-updating');
      info.setAttribute('aria-busy', 'true');
    }
    var prevVariantId = current.variantId;
    current.variantId = String(variant.id);
    load(current.handle, current.variantId, { infoOnly: true, previous: prevVariantId });
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.matches) return;
    if (!form.matches('[data-cm-qv-form]')) return;
    if (!form.closest('#cm-quick-view')) return;
    event.preventDefault();

    var btn = $('[data-cm-qv-add]', form);
    var label = $('[data-cm-qv-add-label]', form);
    if (!btn || btn.disabled || btn.classList.contains('is-loading') || btn.classList.contains('is-added')) return;
    var id = Number($('[data-cm-qv-variant-id]', form).value);
    if (!id) return;
    var msg = $('[data-cm-qv-message]', form);
    if (msg) msg.hidden = true;

    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    if (label) label.textContent = btn.dataset.labelAdding;

    window.CM.cart.add({ id: id, quantity: 1 }).then(function () {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
      btn.classList.add('is-added');
      if (label) label.textContent = btn.dataset.labelAdded;
      setTimeout(function () {
        var modal = getModal();
        if (modal && typeof modal.close === 'function') modal.close();
        if (window.CM && window.CM.emit) window.CM.emit('cart:open', {});
      }, CONFIRM_MS);
    }).catch(function (err) {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
      if (label) label.textContent = btn.dataset.labelAdd;
      if (msg) {
        msg.textContent = getStrings().error || '';
        msg.hidden = false;
      }
    });
  });
})();
